package top.wcpe.allincore.ui.runtime.element

import kotlin.jvm.JvmStatic

/**
 * 缩放视口装饰器：对 body 应用整体缩放，缩放后内容仍可滚动。
 *
 * MVP 实现：通过复用 [UIElement.transform] 的 scaleX/scaleY 实现缩放——设置 [zoom] 时同步写入自身
 * transform 缩放分量。渲染端已按 transform 缩放绘制，命中测试侧 `AffineMatrix3.fromElement` 已读取
 * scale 并做逆变换，故缩放后点击自动对齐，无需额外改 HitTester。
 *
 * [zoomBy] 按 [zoomStep] 步进（滚轮缩放），结果 clamp 到 [minZoom, maxZoom]。
 */
class Zoomable private constructor() : ScrollDecorator<Zoomable>() {

    /** 缩放焦点。 */
    enum class ZoomFocus {
        MOUSE,
        CENTER,
    }

    private var zoomValue = DEFAULT_ZOOM
    private var minZoomValue = DEFAULT_MIN_ZOOM
    private var maxZoomValue = DEFAULT_MAX_ZOOM
    private var zoomStepValue = DEFAULT_ZOOM_STEP
    private var zoomFocusValue = ZoomFocus.CENTER

    protected override fun self(): Zoomable = this

    // -------- 字段 --------

    fun zoom(z: Float): Zoomable {
        zoomValue = clampZoom(z)
        applyTransform()
        return this
    }

    fun zoom(): Float = zoomValue

    fun minZoom(z: Float): Zoomable {
        minZoomValue = maxOf(MIN_ZOOM_FLOOR, z)
        zoomValue = clampZoom(zoomValue)
        applyTransform()
        return this
    }

    fun minZoom(): Float = minZoomValue

    fun maxZoom(z: Float): Zoomable {
        maxZoomValue = maxOf(minZoomValue, z)
        zoomValue = clampZoom(zoomValue)
        applyTransform()
        return this
    }

    fun maxZoom(): Float = maxZoomValue

    fun zoomStep(step: Float): Zoomable {
        zoomStepValue = maxOf(ZOOM_STEP_FLOOR, step)
        return this
    }

    fun zoomStep(): Float = zoomStepValue

    fun zoomFocus(focus: ZoomFocus?): Zoomable {
        zoomFocusValue = focus ?: ZoomFocus.CENTER
        return this
    }

    fun zoomFocus(): ZoomFocus = zoomFocusValue

    // -------- 缩放操作 --------

    /** 按 zoomStep 的 steps 倍步进缩放（滚轮：steps 为正放大、负缩小）。 */
    fun zoomBy(steps: Float): Zoomable = zoom(zoomValue + steps * zoomStepValue)

    // 缩放值钳到 [minZoom, maxZoom]；maxZoom setter 保证 max>=min，故 coerceIn 区间始终合法
    private fun clampZoom(z: Float): Float = z.coerceIn(minZoomValue, maxZoomValue)

    /** 把当前 zoom 写入自身 transform 缩放分量，让渲染与命中测试自动按缩放处理。 */
    private fun applyTransform() {
        transform().setScaleX(zoomValue)
        transform().setScaleY(zoomValue)
    }

    companion object {
        /** 默认缩放倍率。 */
        private const val DEFAULT_ZOOM = 1.0f

        /** 默认最小缩放倍率。 */
        private const val DEFAULT_MIN_ZOOM = 0.5f

        /** 默认最大缩放倍率。 */
        private const val DEFAULT_MAX_ZOOM = 3.0f

        /** 默认步进缩放量。 */
        private const val DEFAULT_ZOOM_STEP = 0.1f

        /** 最小缩放下限（防止 minZoom 设为 0 或负数）。 */
        private const val MIN_ZOOM_FLOOR = 0.01f

        /** 步进缩放下限。 */
        private const val ZOOM_STEP_FLOOR = 0.001f

        /** 创建一个缩放视口装饰器。 */
        @JvmStatic
        fun create(): Zoomable = Zoomable()
    }
}
